import * as i0 from '@angular/core';
import { Directive, HostListener, Component, ChangeDetectionStrategy, NgModule } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { __decorate } from 'tslib';
import { fromEvent } from 'rxjs';
import { defineCustomElements } from '@heartlandone/vega/loader';
import { ResizeObserver } from '@juggle/resize-observer';
import { FeatureFlag } from '@heartlandone/vega';

class ValueAccessor {
    constructor(el) {
        this.el = el;
        this.onChange = () => {
            /**/
        };
        this.onTouched = () => {
            /**/
        };
    }
    writeValue(value) {
        this.el.nativeElement.value = this.lastValue = value == null ? '' : value;
    }
    handleChangeEvent(value) {
        if (value !== this.lastValue) {
            this.lastValue = value;
            this.onChange(value);
        }
    }
    _handleBlurEvent() {
        this.onTouched();
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.el.nativeElement.disabled = isDisabled;
    }
}
ValueAccessor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: ValueAccessor, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
ValueAccessor.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.2.2", type: ValueAccessor, selector: "vega-value-accessor", host: { listeners: { "focusout": "_handleBlurEvent()" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: ValueAccessor, decorators: [{
            type: Directive,
            args: [{ selector: 'vega-value-accessor' }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { _handleBlurEvent: [{
                type: HostListener,
                args: ['focusout']
            }] } });

class TextValueAccessor extends ValueAccessor {
    constructor(el) {
        super(el);
    }
}
TextValueAccessor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: TextValueAccessor, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
TextValueAccessor.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.2.2", type: TextValueAccessor, selector: "vega-checkbox-group, vega-checkbox, vega-color-picker, vega-combo-box, vega-date-picker, vega-image-uploader, vega-input, vega-input-credit-card, vega-input-select, vega-radio-group, vega-stepper, vega-textarea, vega-time-picker", host: { listeners: { "vegaChange": "handleChangeEvent($event.target.value)" } }, providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: TextValueAccessor,
            multi: true
        }
    ], usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: TextValueAccessor, decorators: [{
            type: Directive,
            args: [{
                    /* tslint:disable-next-line:directive-selector */
                    selector: 'vega-checkbox-group, vega-checkbox, vega-color-picker, vega-combo-box, vega-date-picker, vega-image-uploader, vega-input, vega-input-credit-card, vega-input-select, vega-radio-group, vega-stepper, vega-textarea, vega-time-picker',
                    host: {
                        '(vegaChange)': 'handleChangeEvent($event.target.value)'
                    },
                    providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: TextValueAccessor,
                            multi: true
                        }
                    ]
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; } });

/* eslint-disable */
const proxyInputs = (Cmp, inputs) => {
    const Prototype = Cmp.prototype;
    inputs.forEach(item => {
        Object.defineProperty(Prototype, item, {
            get() {
                return this.el[item];
            },
            set(val) {
                // Link issues: https://gethired.atlassian.net/browse/GHUI-331
                // add a beforehand checking to make sure value is only set when component is loaded
                if (this.el.classList.contains('hydrated')) {
                    this.z.runOutsideAngular(() => (this.el[item] = val));
                }
                else {
                    this.el.componentOnReady().then(() => {
                        this.z.runOutsideAngular(() => (this.el[item] = val));
                    });
                }
            }
        });
    });
};
const proxyMethods = (Cmp, methods) => {
    const Prototype = Cmp.prototype;
    methods.forEach(methodName => {
        Prototype[methodName] = function () {
            const args = arguments;
            return this.z.runOutsideAngular(() => this.el[methodName].apply(this.el, args));
        };
    });
};
const proxyOutputs = (instance, el, events) => {
    events.forEach(eventName => instance[eventName] = fromEvent(el, eventName));
};
const defineCustomElement = (tagName, customElement) => {
    if (customElement !== undefined &&
        typeof customElements !== 'undefined' &&
        !customElements.get(tagName)) {
        customElements.define(tagName, customElement);
    }
};
// tslint:disable-next-line: only-arrow-functions
function ProxyCmp(opts) {
    const decorator = function (cls) {
        const { defineCustomElementFn, inputs, methods } = opts;
        if (defineCustomElementFn !== undefined) {
            defineCustomElementFn();
        }
        if (inputs) {
            proxyInputs(cls, inputs);
        }
        if (methods) {
            proxyMethods(cls, methods);
        }
        return cls;
    };
    return decorator;
}

let VegaAccordion = class VegaAccordion {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['vegaExpand']);
    }
};
VegaAccordion.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaAccordion, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaAccordion.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaAccordion, selector: "vega-accordion", inputs: { accordionTitle: "accordionTitle", drawsBackground: "drawsBackground", expand: "expand", expandIconAlign: "expandIconAlign", groupId: "groupId", iconPosition: "iconPosition", prefixIcon: "prefixIcon", showExpandIcon: "showExpandIcon" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaAccordion = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['accordionTitle', 'drawsBackground', 'expand', 'expandIconAlign', 'groupId', 'iconPosition', 'prefixIcon', 'showExpandIcon']
    })
], VegaAccordion);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaAccordion, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-accordion',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['accordionTitle', 'drawsBackground', 'expand', 'expandIconAlign', 'groupId', 'iconPosition', 'prefixIcon', 'showExpandIcon']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaAppFooter = class VegaAppFooter {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
VegaAppFooter.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaAppFooter, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaAppFooter.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaAppFooter, selector: "vega-app-footer", inputs: { additionalLinks: "additionalLinks", content: "content", disclaimerUrlTarget: "disclaimerUrlTarget", hideDefaultLinks: "hideDefaultLinks", privacyPolicyLabel: "privacyPolicyLabel", privacyPolicyUrl: "privacyPolicyUrl", privacyPolicyUrlTarget: "privacyPolicyUrlTarget", showDisclaimer: "showDisclaimer", termOfUseLabel: "termOfUseLabel", termOfUseUrl: "termOfUseUrl", termOfUseUrlTarget: "termOfUseUrlTarget" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaAppFooter = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['additionalLinks', 'content', 'disclaimerUrlTarget', 'hideDefaultLinks', 'privacyPolicyLabel', 'privacyPolicyUrl', 'privacyPolicyUrlTarget', 'showDisclaimer', 'termOfUseLabel', 'termOfUseUrl', 'termOfUseUrlTarget']
    })
], VegaAppFooter);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaAppFooter, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-app-footer',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['additionalLinks', 'content', 'disclaimerUrlTarget', 'hideDefaultLinks', 'privacyPolicyLabel', 'privacyPolicyUrl', 'privacyPolicyUrlTarget', 'showDisclaimer', 'termOfUseLabel', 'termOfUseUrl', 'termOfUseUrlTarget']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaAppHeaderButton = class VegaAppHeaderButton {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['vegaClick', 'vegaDropdownClick']);
    }
};
VegaAppHeaderButton.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaAppHeaderButton, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaAppHeaderButton.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaAppHeaderButton, selector: "vega-app-header-button", inputs: { avatar: "avatar", avatarPosition: "avatarPosition", dropdown: "dropdown", label: "label", selected: "selected", showBorder: "showBorder", showLabel: "showLabel", showMenuArrowIcon: "showMenuArrowIcon", size: "size" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaAppHeaderButton = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['avatar', 'avatarPosition', 'dropdown', 'label', 'selected', 'showBorder', 'showLabel', 'showMenuArrowIcon', 'size']
    })
], VegaAppHeaderButton);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaAppHeaderButton, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-app-header-button',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['avatar', 'avatarPosition', 'dropdown', 'label', 'selected', 'showBorder', 'showLabel', 'showMenuArrowIcon', 'size']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaBadge = class VegaBadge {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
VegaBadge.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaBadge, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaBadge.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaBadge, selector: "vega-badge", inputs: { bgColor: "bgColor", size: "size", text: "text", textColor: "textColor" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaBadge = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['bgColor', 'size', 'text', 'textColor']
    })
], VegaBadge);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaBadge, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-badge',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['bgColor', 'size', 'text', 'textColor']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaBanner = class VegaBanner {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['vegaClose', 'vegaLoad', 'vegaClickCTA']);
    }
};
VegaBanner.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaBanner, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaBanner.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaBanner, selector: "vega-banner", inputs: { mode: "mode", source: "source" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaBanner = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['mode', 'source']
    })
], VegaBanner);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaBanner, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-banner',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['mode', 'source']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaBarChart = class VegaBarChart {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
VegaBarChart.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaBarChart, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaBarChart.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaBarChart, selector: "vega-bar-chart", inputs: { options: "options" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaBarChart = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['options']
    })
], VegaBarChart);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaBarChart, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-bar-chart',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['options']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaBox = class VegaBox {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
VegaBox.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaBox, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaBox.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaBox, selector: "vega-box", inputs: { display: "display", margin: "margin", padding: "padding" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaBox = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['display', 'margin', 'padding']
    })
], VegaBox);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaBox, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-box',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['display', 'margin', 'padding']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaButton = class VegaButton {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['vegaClick']);
    }
};
VegaButton.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaButton, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaButton.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaButton, selector: "vega-button", inputs: { block: "block", danger: "danger", disabled: "disabled", icon: "icon", iconAlign: "iconAlign", label: "label", size: "size", type: "type", variant: "variant" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaButton = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['block', 'danger', 'disabled', 'icon', 'iconAlign', 'label', 'size', 'type', 'variant']
    })
], VegaButton);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaButton, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-button',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['block', 'danger', 'disabled', 'icon', 'iconAlign', 'label', 'size', 'type', 'variant']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaButtonCircle = class VegaButtonCircle {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['vegaClick']);
    }
};
VegaButtonCircle.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaButtonCircle, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaButtonCircle.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaButtonCircle, selector: "vega-button-circle", inputs: { danger: "danger", disabled: "disabled", icon: "icon", iconColor: "iconColor", label: "label", size: "size", type: "type", variant: "variant" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaButtonCircle = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['danger', 'disabled', 'icon', 'iconColor', 'label', 'size', 'type', 'variant']
    })
], VegaButtonCircle);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaButtonCircle, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-button-circle',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['danger', 'disabled', 'icon', 'iconColor', 'label', 'size', 'type', 'variant']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaButtonLink = class VegaButtonLink {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['vegaClick']);
    }
};
VegaButtonLink.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaButtonLink, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaButtonLink.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaButtonLink, selector: "vega-button-link", inputs: { danger: "danger", disabled: "disabled", eventful: "eventful", icon: "icon", iconAlign: "iconAlign", iconType: "iconType", label: "label", link: "link", size: "size" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaButtonLink = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['danger', 'disabled', 'eventful', 'icon', 'iconAlign', 'iconType', 'label', 'link', 'size']
    })
], VegaButtonLink);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaButtonLink, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-button-link',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['danger', 'disabled', 'eventful', 'icon', 'iconAlign', 'iconType', 'label', 'link', 'size']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaCard = class VegaCard {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
VegaCard.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaCard, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaCard.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaCard, selector: "vega-card", inputs: { backgroundColor: "backgroundColor", margin: "margin", padding: "padding", shadow: "shadow", variant: "variant" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaCard = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['backgroundColor', 'margin', 'padding', 'shadow', 'variant']
    })
], VegaCard);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaCard, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-card',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['backgroundColor', 'margin', 'padding', 'shadow', 'variant']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaCarousel = class VegaCarousel {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['vegaPageUpdate']);
    }
};
VegaCarousel.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaCarousel, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaCarousel.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaCarousel, selector: "vega-carousel", inputs: { canSwipe: "canSwipe", perPage: "perPage", showSlider: "showSlider", spacing: "spacing" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaCarousel = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['canSwipe', 'perPage', 'showSlider', 'spacing'],
        methods: ['nextPage', 'prevPage', 'goToPage']
    })
], VegaCarousel);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaCarousel, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-carousel',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['canSwipe', 'perPage', 'showSlider', 'spacing']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaCheckbox = class VegaCheckbox {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['vegaValidate', 'vegaChange']);
    }
};
VegaCheckbox.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaCheckbox, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaCheckbox.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaCheckbox, selector: "vega-checkbox", inputs: { checked: "checked", disabled: "disabled", identifier: "identifier", isValid: "isValid", required: "required", size: "size", value: "value" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaCheckbox = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['checked', 'disabled', 'identifier', 'isValid', 'required', 'size', 'value'],
        methods: ['valid', 'setValue']
    })
], VegaCheckbox);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaCheckbox, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-checkbox',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['checked', 'disabled', 'identifier', 'isValid', 'required', 'size', 'value']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaCheckboxGroup = class VegaCheckboxGroup {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['vegaChange', 'vegaValidate']);
    }
};
VegaCheckboxGroup.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaCheckboxGroup, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaCheckboxGroup.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaCheckboxGroup, selector: "vega-checkbox-group", inputs: { disabled: "disabled", formValidation: "formValidation", hint: "hint", isValid: "isValid", label: "label", required: "required", value: "value", vegaFlexProp: "vegaFlexProp" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaCheckboxGroup = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['disabled', 'formValidation', 'hint', 'isValid', 'label', 'required', 'value', 'vegaFlexProp'],
        methods: ['valid']
    })
], VegaCheckboxGroup);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaCheckboxGroup, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-checkbox-group',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['disabled', 'formValidation', 'hint', 'isValid', 'label', 'required', 'value', 'vegaFlexProp']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaChip = class VegaChip {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['vegaClose', 'vegaClick']);
    }
};
VegaChip.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaChip, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaChip.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaChip, selector: "vega-chip", inputs: { bgColor: "bgColor", chipType: "chipType", clickable: "clickable", icon: "icon", iconAlign: "iconAlign", showCloseIcon: "showCloseIcon", size: "size", statusColor: "statusColor", text: "text", textColor: "textColor", variant: "variant" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaChip = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['bgColor', 'chipType', 'clickable', 'icon', 'iconAlign', 'showCloseIcon', 'size', 'statusColor', 'text', 'textColor', 'variant']
    })
], VegaChip);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaChip, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-chip',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['bgColor', 'chipType', 'clickable', 'icon', 'iconAlign', 'showCloseIcon', 'size', 'statusColor', 'text', 'textColor', 'variant']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaColorPicker = class VegaColorPicker {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['vegaChange', 'vegaValidate']);
    }
};
VegaColorPicker.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaColorPicker, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaColorPicker.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaColorPicker, selector: "vega-color-picker", inputs: { autoValidation: "autoValidation", colors: "colors", disabled: "disabled", hint: "hint", isValid: "isValid", label: "label", required: "required", validationRules: "validationRules", value: "value" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaColorPicker = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['autoValidation', 'colors', 'disabled', 'hint', 'isValid', 'label', 'required', 'validationRules', 'value'],
        methods: ['valid']
    })
], VegaColorPicker);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaColorPicker, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-color-picker',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['autoValidation', 'colors', 'disabled', 'hint', 'isValid', 'label', 'required', 'validationRules', 'value']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaComboBox = class VegaComboBox {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['vegaChange', 'vegaValidate', 'vegaCreate', 'vegaSearch']);
    }
};
VegaComboBox.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaComboBox, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaComboBox.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaComboBox, selector: "vega-combo-box", inputs: { autoValidation: "autoValidation", disabled: "disabled", hint: "hint", isValid: "isValid", label: "label", placeholder: "placeholder", required: "required", size: "size", source: "source", useDefaultFilter: "useDefaultFilter", validationRules: "validationRules", value: "value", vegaDropdownProps: "vegaDropdownProps" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaComboBox = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['autoValidation', 'disabled', 'hint', 'isValid', 'label', 'placeholder', 'required', 'size', 'source', 'useDefaultFilter', 'validationRules', 'value', 'vegaDropdownProps'],
        methods: ['close', 'open', 'valid']
    })
], VegaComboBox);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaComboBox, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-combo-box',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['autoValidation', 'disabled', 'hint', 'isValid', 'label', 'placeholder', 'required', 'size', 'source', 'useDefaultFilter', 'validationRules', 'value', 'vegaDropdownProps']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaCounterBadge = class VegaCounterBadge {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
VegaCounterBadge.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaCounterBadge, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaCounterBadge.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaCounterBadge, selector: "vega-counter-badge", inputs: { color: "color", count: "count", dot: "dot", offset: "offset" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaCounterBadge = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['color', 'count', 'dot', 'offset']
    })
], VegaCounterBadge);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaCounterBadge, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-counter-badge',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['color', 'count', 'dot', 'offset']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaDatePicker = class VegaDatePicker {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['vegaChange', 'vegaValidate']);
    }
};
VegaDatePicker.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaDatePicker, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaDatePicker.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaDatePicker, selector: "vega-date-picker", inputs: { allowRepick: "allowRepick", autoValidation: "autoValidation", clearButton: "clearButton", disabled: "disabled", dropdownConfig: "dropdownConfig", formValidation: "formValidation", format: "format", isValid: "isValid", label: "label", maxDate: "maxDate", minDate: "minDate", mode: "mode", placeholder: "placeholder", readOnly: "readOnly", required: "required", size: "size", validationRules: "validationRules", value: "value" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaDatePicker = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['allowRepick', 'autoValidation', 'clearButton', 'disabled', 'dropdownConfig', 'formValidation', 'format', 'isValid', 'label', 'maxDate', 'minDate', 'mode', 'placeholder', 'readOnly', 'required', 'size', 'validationRules', 'value'],
        methods: ['valid', 'doClose', 'doOpen']
    })
], VegaDatePicker);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaDatePicker, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-date-picker',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['allowRepick', 'autoValidation', 'clearButton', 'disabled', 'dropdownConfig', 'formValidation', 'format', 'isValid', 'label', 'maxDate', 'minDate', 'mode', 'placeholder', 'readOnly', 'required', 'size', 'validationRules', 'value']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaDialog = class VegaDialog {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
VegaDialog.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaDialog, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaDialog.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaDialog, selector: "vega-dialog", inputs: { cancelButton: "cancelButton", content: "content", dialogTitle: "dialogTitle", handleCancel: "handleCancel", handleOk: "handleOk", modalProps: "modalProps", okButton: "okButton", showCancel: "showCancel", type: "type" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaDialog = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['cancelButton', 'content', 'dialogTitle', 'handleCancel', 'handleOk', 'modalProps', 'okButton', 'showCancel', 'type']
    })
], VegaDialog);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaDialog, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-dialog',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['cancelButton', 'content', 'dialogTitle', 'handleCancel', 'handleOk', 'modalProps', 'okButton', 'showCancel', 'type']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaDropdown = class VegaDropdown {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['vegaClick', 'vegaDropdownClick', 'vegaShow', 'vegaHide', 'vegaCreate', 'vegaSearch']);
    }
};
VegaDropdown.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaDropdown, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaDropdown.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaDropdown, selector: "vega-dropdown", inputs: { caseSensitive: "caseSensitive", dynamicOption: "dynamicOption", isLoading: "isLoading", itemDisplayRule: "itemDisplayRule", matchContainerHeight: "matchContainerHeight", matchTargetWidth: "matchTargetWidth", maxHeight: "maxHeight", maxWidth: "maxWidth", minWidth: "minWidth", positionRelativeTo: "positionRelativeTo", searchable: "searchable", selectType: "selectType", selectedSourceKey: "selectedSourceKey", size: "size", source: "source", translocation: "translocation", trigger: "trigger", useDefaultFilter: "useDefaultFilter" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaDropdown = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['caseSensitive', 'dynamicOption', 'isLoading', 'itemDisplayRule', 'matchContainerHeight', 'matchTargetWidth', 'maxHeight', 'maxWidth', 'minWidth', 'positionRelativeTo', 'searchable', 'selectType', 'selectedSourceKey', 'size', 'source', 'translocation', 'trigger', 'useDefaultFilter'],
        methods: ['show', 'hide', 'search']
    })
], VegaDropdown);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaDropdown, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-dropdown',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['caseSensitive', 'dynamicOption', 'isLoading', 'itemDisplayRule', 'matchContainerHeight', 'matchTargetWidth', 'maxHeight', 'maxWidth', 'minWidth', 'positionRelativeTo', 'searchable', 'selectType', 'selectedSourceKey', 'size', 'source', 'translocation', 'trigger', 'useDefaultFilter']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaFieldLabel = class VegaFieldLabel {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
VegaFieldLabel.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaFieldLabel, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaFieldLabel.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaFieldLabel, selector: "vega-field-label", inputs: { disabled: "disabled", isFieldRequired: "isFieldRequired", label: "label" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaFieldLabel = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['disabled', 'isFieldRequired', 'label']
    })
], VegaFieldLabel);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaFieldLabel, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-field-label',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['disabled', 'isFieldRequired', 'label']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaFlex = class VegaFlex {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
VegaFlex.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaFlex, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaFlex.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaFlex, selector: "vega-flex", inputs: { alignItem: "alignItem", breakpoint: "breakpoint", direction: "direction", gap: "gap", justifyContent: "justifyContent", margin: "margin", useNativeFlex: "useNativeFlex" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaFlex = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['alignItem', 'breakpoint', 'direction', 'gap', 'justifyContent', 'margin', 'useNativeFlex']
    })
], VegaFlex);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaFlex, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-flex',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['alignItem', 'breakpoint', 'direction', 'gap', 'justifyContent', 'margin', 'useNativeFlex']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaFooter = class VegaFooter {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
VegaFooter.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaFooter, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaFooter.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaFooter, selector: "vega-footer", inputs: { privacyUrl: "privacyUrl", tosUrl: "tosUrl" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaFooter = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['privacyUrl', 'tosUrl']
    })
], VegaFooter);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaFooter, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-footer',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['privacyUrl', 'tosUrl']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaForm = class VegaForm {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['vegaValidate', 'vegaFormSubmit', 'vegaFormReset']);
    }
};
VegaForm.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaForm, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaForm.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaForm, selector: "vega-form", inputs: { isTouched: "isTouched", isValid: "isValid" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaForm = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['isTouched', 'isValid'],
        methods: ['valid', 'getValue', 'setValue', 'reset']
    })
], VegaForm);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaForm, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-form',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['isTouched', 'isValid']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaGrid = class VegaGrid {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
VegaGrid.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaGrid, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaGrid.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaGrid, selector: "vega-grid", inputs: { column: "column", gap: "gap", row: "row" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaGrid = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['column', 'gap', 'row']
    })
], VegaGrid);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaGrid, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-grid',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['column', 'gap', 'row']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaHeader = class VegaHeader {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
VegaHeader.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaHeader, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaHeader.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaHeader, selector: "vega-header", ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaHeader = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined
    })
], VegaHeader);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaHeader, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-header',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>'
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaIcon = class VegaIcon {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
VegaIcon.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaIcon, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaIcon.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaIcon, selector: "vega-icon", inputs: { color: "color", icon: "icon", size: "size" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaIcon = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['color', 'icon', 'size']
    })
], VegaIcon);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaIcon, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-icon',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['color', 'icon', 'size']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaImageUploader = class VegaImageUploader {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['vegaChange', 'vegaRemove', 'vegaCancel', 'vegaValidate']);
    }
};
VegaImageUploader.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaImageUploader, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaImageUploader.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaImageUploader, selector: "vega-image-uploader", inputs: { accept: "accept", autoValidation: "autoValidation", disabled: "disabled", isValid: "isValid", required: "required", showPreviewButton: "showPreviewButton", showRemoveButton: "showRemoveButton", status: "status", validationRules: "validationRules", value: "value" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaImageUploader = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['accept', 'autoValidation', 'disabled', 'isValid', 'required', 'showPreviewButton', 'showRemoveButton', 'status', 'validationRules', 'value'],
        methods: ['valid']
    })
], VegaImageUploader);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaImageUploader, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-image-uploader',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['accept', 'autoValidation', 'disabled', 'isValid', 'required', 'showPreviewButton', 'showRemoveButton', 'status', 'validationRules', 'value']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaInput = class VegaInput {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['vegaChange', 'vegaValidate', 'vegaBlur', 'vegaFocus']);
    }
};
VegaInput.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaInput, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaInput.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaInput, selector: "vega-input", inputs: { autoValidation: "autoValidation", autocomplete: "autocomplete", disabled: "disabled", email: "email", formValidation: "formValidation", hint: "hint", isValid: "isValid", label: "label", maskConfig: "maskConfig", max: "max", maxLength: "maxLength", min: "min", minLength: "minLength", placeholder: "placeholder", prefixIcon: "prefixIcon", required: "required", showClearIcon: "showClearIcon", size: "size", type: "type", validationRules: "validationRules", value: "value" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaInput = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['autoValidation', 'autocomplete', 'disabled', 'email', 'formValidation', 'hint', 'isValid', 'label', 'maskConfig', 'max', 'maxLength', 'min', 'minLength', 'placeholder', 'prefixIcon', 'required', 'showClearIcon', 'size', 'type', 'validationRules', 'value'],
        methods: ['valid', 'doFocus', 'doBlur']
    })
], VegaInput);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaInput, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-input',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['autoValidation', 'autocomplete', 'disabled', 'email', 'formValidation', 'hint', 'isValid', 'label', 'maskConfig', 'max', 'maxLength', 'min', 'minLength', 'placeholder', 'prefixIcon', 'required', 'showClearIcon', 'size', 'type', 'validationRules', 'value']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaInputCreditCard = class VegaInputCreditCard {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['vegaValidate', 'vegaChange', 'vegaBlur', 'vegaFocus']);
    }
};
VegaInputCreditCard.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaInputCreditCard, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaInputCreditCard.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaInputCreditCard, selector: "vega-input-credit-card", inputs: { autoValidation: "autoValidation", disabled: "disabled", hideCardNumberOnBlur: "hideCardNumberOnBlur", hint: "hint", isValid: "isValid", label: "label", placeholder: "placeholder", required: "required", size: "size", validationRules: "validationRules", value: "value" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaInputCreditCard = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['autoValidation', 'disabled', 'hideCardNumberOnBlur', 'hint', 'isValid', 'label', 'placeholder', 'required', 'size', 'validationRules', 'value'],
        methods: ['valid']
    })
], VegaInputCreditCard);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaInputCreditCard, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-input-credit-card',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['autoValidation', 'disabled', 'hideCardNumberOnBlur', 'hint', 'isValid', 'label', 'placeholder', 'required', 'size', 'validationRules', 'value']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaInputSelect = class VegaInputSelect {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['vegaChange', 'vegaValidate']);
    }
};
VegaInputSelect.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaInputSelect, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaInputSelect.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaInputSelect, selector: "vega-input-select", inputs: { autoValidation: "autoValidation", disabled: "disabled", formValidation: "formValidation", hint: "hint", isValid: "isValid", label: "label", placeholder: "placeholder", prefixIcon: "prefixIcon", required: "required", selectType: "selectType", selectedLabel: "selectedLabel", size: "size", source: "source", validationRules: "validationRules", value: "value", vegaDropdownProps: "vegaDropdownProps" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaInputSelect = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['autoValidation', 'disabled', 'formValidation', 'hint', 'isValid', 'label', 'placeholder', 'prefixIcon', 'required', 'selectType', 'selectedLabel', 'size', 'source', 'validationRules', 'value', 'vegaDropdownProps'],
        methods: ['doClose', 'doOpen', 'doChange', 'valid']
    })
], VegaInputSelect);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaInputSelect, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-input-select',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['autoValidation', 'disabled', 'formValidation', 'hint', 'isValid', 'label', 'placeholder', 'prefixIcon', 'required', 'selectType', 'selectedLabel', 'size', 'source', 'validationRules', 'value', 'vegaDropdownProps']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaItemToggle = class VegaItemToggle {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['vegaToggleStatus']);
    }
};
VegaItemToggle.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaItemToggle, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaItemToggle.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaItemToggle, selector: "vega-item-toggle", inputs: { isError: "isError", isToggled: "isToggled", itemToggleStatusMap: "itemToggleStatusMap" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaItemToggle = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['isError', 'isToggled', 'itemToggleStatusMap']
    })
], VegaItemToggle);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaItemToggle, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-item-toggle',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['isError', 'isToggled', 'itemToggleStatusMap']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaLeftNav = class VegaLeftNav {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['vegaStateUpdate']);
    }
};
VegaLeftNav.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaLeftNav, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaLeftNav.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaLeftNav, selector: "vega-left-nav", inputs: { footnote: "footnote", headerConfig: "headerConfig", showAsOverlay: "showAsOverlay", source: "source" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaLeftNav = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['footnote', 'headerConfig', 'showAsOverlay', 'source'],
        methods: ['toggle']
    })
], VegaLeftNav);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaLeftNav, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-left-nav',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['footnote', 'headerConfig', 'showAsOverlay', 'source']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaLeftNavGroup = class VegaLeftNavGroup {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
VegaLeftNavGroup.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaLeftNavGroup, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaLeftNavGroup.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaLeftNavGroup, selector: "vega-left-nav-group", inputs: { icon: "icon", label: "label" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaLeftNavGroup = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['icon', 'label']
    })
], VegaLeftNavGroup);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaLeftNavGroup, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-left-nav-group',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['icon', 'label']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaLeftNavLink = class VegaLeftNavLink {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['vegaMenuClick']);
    }
};
VegaLeftNavLink.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaLeftNavLink, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaLeftNavLink.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaLeftNavLink, selector: "vega-left-nav-link", inputs: { counterBadge: "counterBadge", icon: "icon", selected: "selected", url: "url" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaLeftNavLink = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['counterBadge', 'icon', 'selected', 'url']
    })
], VegaLeftNavLink);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaLeftNavLink, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-left-nav-link',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['counterBadge', 'icon', 'selected', 'url']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaLeftNavSection = class VegaLeftNavSection {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
VegaLeftNavSection.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaLeftNavSection, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaLeftNavSection.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaLeftNavSection, selector: "vega-left-nav-section", inputs: { sectionHeader: "sectionHeader" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaLeftNavSection = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['sectionHeader']
    })
], VegaLeftNavSection);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaLeftNavSection, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-left-nav-section',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['sectionHeader']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaLineChart = class VegaLineChart {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
VegaLineChart.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaLineChart, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaLineChart.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaLineChart, selector: "vega-line-chart", inputs: { options: "options" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaLineChart = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['options'],
        methods: ['reRender']
    })
], VegaLineChart);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaLineChart, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-line-chart',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['options']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaLoadingIndicator = class VegaLoadingIndicator {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
VegaLoadingIndicator.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaLoadingIndicator, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaLoadingIndicator.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaLoadingIndicator, selector: "vega-loading-indicator", inputs: { hint: "hint", label: "label", mode: "mode", percent: "percent", shape: "shape", size: "size", status: "status" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaLoadingIndicator = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['hint', 'label', 'mode', 'percent', 'shape', 'size', 'status']
    })
], VegaLoadingIndicator);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaLoadingIndicator, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-loading-indicator',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['hint', 'label', 'mode', 'percent', 'shape', 'size', 'status']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaModal = class VegaModal {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['vegaClose', 'vegaOpen']);
    }
};
VegaModal.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaModal, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaModal.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaModal, selector: "vega-modal", inputs: { animation: "animation", backdrop: "backdrop", backdropColor: "backdropColor", contentMaxHeight: "contentMaxHeight", handleClose: "handleClose", isVerticallyCenter: "isVerticallyCenter", modalTitle: "modalTitle", open: "open", padding: "padding", showCloseButton: "showCloseButton", size: "size" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaModal = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['animation', 'backdrop', 'backdropColor', 'contentMaxHeight', 'handleClose', 'isVerticallyCenter', 'modalTitle', 'open', 'padding', 'showCloseButton', 'size'],
        methods: ['modal']
    })
], VegaModal);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaModal, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-modal',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['animation', 'backdrop', 'backdropColor', 'contentMaxHeight', 'handleClose', 'isVerticallyCenter', 'modalTitle', 'open', 'padding', 'showCloseButton', 'size']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaOption = class VegaOption {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
VegaOption.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaOption, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaOption.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaOption, selector: "vega-option", inputs: { icon: "icon", label: "label", subLabel: "subLabel" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaOption = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['icon', 'label', 'subLabel']
    })
], VegaOption);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaOption, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-option',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['icon', 'label', 'subLabel']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaPageNotification = class VegaPageNotification {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
VegaPageNotification.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaPageNotification, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaPageNotification.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaPageNotification, selector: "vega-page-notification", inputs: { actionButtons: "actionButtons", duration: "duration", message: "message", notificationTitle: "notificationTitle", showCloseButton: "showCloseButton", type: "type" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaPageNotification = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['actionButtons', 'duration', 'message', 'notificationTitle', 'showCloseButton', 'type'],
        methods: ['close']
    })
], VegaPageNotification);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaPageNotification, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-page-notification',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['actionButtons', 'duration', 'message', 'notificationTitle', 'showCloseButton', 'type']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaPagination = class VegaPagination {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['vegaChange', 'vegaPageSizeChange']);
    }
};
VegaPagination.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaPagination, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaPagination.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaPagination, selector: "vega-pagination", inputs: { current: "current", extensions: "extensions", pageSize: "pageSize", pageSizeSelectorOptions: "pageSizeSelectorOptions", total: "total" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaPagination = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['current', 'extensions', 'pageSize', 'pageSizeSelectorOptions', 'total']
    })
], VegaPagination);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaPagination, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-pagination',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['current', 'extensions', 'pageSize', 'pageSizeSelectorOptions', 'total']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaPieChart = class VegaPieChart {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
VegaPieChart.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaPieChart, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaPieChart.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaPieChart, selector: "vega-pie-chart", inputs: { options: "options" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaPieChart = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['options'],
        methods: ['reRender']
    })
], VegaPieChart);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaPieChart, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-pie-chart',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['options']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaPopover = class VegaPopover {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['vegaPopoverShow', 'vegaPopoverHide']);
    }
};
VegaPopover.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaPopover, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaPopover.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaPopover, selector: "vega-popover", inputs: { alignment: "alignment", placement: "placement", positionRelativeTo: "positionRelativeTo", size: "size" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaPopover = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['alignment', 'placement', 'positionRelativeTo', 'size'],
        methods: ['show', 'hide']
    })
], VegaPopover);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaPopover, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-popover',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['alignment', 'placement', 'positionRelativeTo', 'size']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaProgressTracker = class VegaProgressTracker {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['vegaCurrentStepUpdate']);
    }
};
VegaProgressTracker.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaProgressTracker, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaProgressTracker.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaProgressTracker, selector: "vega-progress-tracker", inputs: { canClickStep: "canClickStep", completedStepArray: "completedStepArray", current: "current", direction: "direction", steps: "steps" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaProgressTracker = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['canClickStep', 'completedStepArray', 'current', 'direction', 'steps'],
        methods: ['nextStep', 'prevStep', 'setCurrent', 'getCurrent']
    })
], VegaProgressTracker);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaProgressTracker, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-progress-tracker',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['canClickStep', 'completedStepArray', 'current', 'direction', 'steps']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaRadio = class VegaRadio {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['vegaFocus', 'vegaBlur', 'vegaChange']);
    }
};
VegaRadio.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaRadio, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaRadio.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaRadio, selector: "vega-radio", inputs: { checked: "checked", disabled: "disabled", name: "name", size: "size", value: "value" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaRadio = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['checked', 'disabled', 'name', 'size', 'value']
    })
], VegaRadio);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaRadio, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-radio',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['checked', 'disabled', 'name', 'size', 'value']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaRadioGroup = class VegaRadioGroup {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['vegaChange', 'vegaValidate']);
    }
};
VegaRadioGroup.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaRadioGroup, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaRadioGroup.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaRadioGroup, selector: "vega-radio-group", inputs: { disabled: "disabled", formValidation: "formValidation", hint: "hint", isValid: "isValid", label: "label", name: "name", required: "required", value: "value", vegaFlexProp: "vegaFlexProp" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaRadioGroup = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['disabled', 'formValidation', 'hint', 'isValid', 'label', 'name', 'required', 'value', 'vegaFlexProp'],
        methods: ['valid']
    })
], VegaRadioGroup);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaRadioGroup, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-radio-group',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['disabled', 'formValidation', 'hint', 'isValid', 'label', 'name', 'required', 'value', 'vegaFlexProp']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaSidenav = class VegaSidenav {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['vegaStateUpdate']);
    }
};
VegaSidenav.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaSidenav, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaSidenav.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaSidenav, selector: "vega-sidenav", inputs: { footnote: "footnote", headerConfig: "headerConfig", openByDefault: "openByDefault", source: "source" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaSidenav = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['footnote', 'headerConfig', 'openByDefault', 'source'],
        methods: ['toggle']
    })
], VegaSidenav);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaSidenav, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-sidenav',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['footnote', 'headerConfig', 'openByDefault', 'source']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaSidenavGroup = class VegaSidenavGroup {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
VegaSidenavGroup.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaSidenavGroup, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaSidenavGroup.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaSidenavGroup, selector: "vega-sidenav-group", inputs: { icon: "icon", label: "label" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaSidenavGroup = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['icon', 'label']
    })
], VegaSidenavGroup);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaSidenavGroup, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-sidenav-group',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['icon', 'label']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaSidenavLink = class VegaSidenavLink {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['vegaClick']);
    }
};
VegaSidenavLink.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaSidenavLink, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaSidenavLink.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaSidenavLink, selector: "vega-sidenav-link", inputs: { icon: "icon", selected: "selected", showSideBarOnClickLink: "showSideBarOnClickLink", url: "url" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaSidenavLink = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['icon', 'selected', 'showSideBarOnClickLink', 'url']
    })
], VegaSidenavLink);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaSidenavLink, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-sidenav-link',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['icon', 'selected', 'showSideBarOnClickLink', 'url']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaStepper = class VegaStepper {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['vegaChange', 'vegaValidate']);
    }
};
VegaStepper.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaStepper, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaStepper.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaStepper, selector: "vega-stepper", inputs: { autoValidation: "autoValidation", buttonVariant: "buttonVariant", disabled: "disabled", formValidation: "formValidation", inputDisabled: "inputDisabled", inputEditable: "inputEditable", isValid: "isValid", label: "label", max: "max", min: "min", stepperId: "stepperId", value: "value" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaStepper = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['autoValidation', 'buttonVariant', 'disabled', 'formValidation', 'inputDisabled', 'inputEditable', 'isValid', 'label', 'max', 'min', 'stepperId', 'value'],
        methods: ['valid']
    })
], VegaStepper);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaStepper, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-stepper',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['autoValidation', 'buttonVariant', 'disabled', 'formValidation', 'inputDisabled', 'inputEditable', 'isValid', 'label', 'max', 'min', 'stepperId', 'value']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaTabGroup = class VegaTabGroup {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['vegaClick']);
    }
};
VegaTabGroup.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaTabGroup, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaTabGroup.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaTabGroup, selector: "vega-tab-group", inputs: { gap: "gap", positionRelativeTo: "positionRelativeTo", selectedTabDataTarget: "selectedTabDataTarget", showCloseButton: "showCloseButton", tabItems: "tabItems" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaTabGroup = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['gap', 'positionRelativeTo', 'selectedTabDataTarget', 'showCloseButton', 'tabItems']
    })
], VegaTabGroup);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaTabGroup, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-tab-group',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['gap', 'positionRelativeTo', 'selectedTabDataTarget', 'showCloseButton', 'tabItems']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaTabGroupPanel = class VegaTabGroupPanel {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
VegaTabGroupPanel.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaTabGroupPanel, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaTabGroupPanel.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaTabGroupPanel, selector: "vega-tab-group-panel", inputs: { isActive: "isActive" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaTabGroupPanel = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['isActive']
    })
], VegaTabGroupPanel);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaTabGroupPanel, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-tab-group-panel',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['isActive']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaTable = class VegaTable {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['vegaChange', 'vegaRowClick', 'vegaSelectChange', 'vegaSelectAllChange']);
    }
};
VegaTable.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaTable, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaTable.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaTable, selector: "vega-table", inputs: { columnDivider: "columnDivider", columns: "columns", dataSource: "dataSource", density: "density", emptyDescription: "emptyDescription", paddingX: "paddingX", pagination: "pagination", rowClickable: "rowClickable", rowDivider: "rowDivider", rowExpandable: "rowExpandable", rowKey: "rowKey", rowSelection: "rowSelection", scrollConfig: "scrollConfig", showHeader: "showHeader", striped: "striped" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaTable = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['columnDivider', 'columns', 'dataSource', 'density', 'emptyDescription', 'paddingX', 'pagination', 'rowClickable', 'rowDivider', 'rowExpandable', 'rowKey', 'rowSelection', 'scrollConfig', 'showHeader', 'striped'],
        methods: ['openExpandRow', 'closeExpandRow', 'closeAllExpandRow', 'toggleExpandRow', 'clearSelection', 'getSelection', 'setSelection', 'startRowEditMode', 'stopRowEditMode', 'stopAllRowEditMode', 'getRowData', 'getRowsData', 'getFormRef', 'addNewRow', 'saveEditRow', 'saveAllEditRows', 'getEditingRowsKey', 'removeRow']
    })
], VegaTable);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaTable, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-table',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['columnDivider', 'columns', 'dataSource', 'density', 'emptyDescription', 'paddingX', 'pagination', 'rowClickable', 'rowDivider', 'rowExpandable', 'rowKey', 'rowSelection', 'scrollConfig', 'showHeader', 'striped']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaText = class VegaText {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
VegaText.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaText, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaText.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaText, selector: "vega-text", inputs: { overflow: "overflow" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaText = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['overflow']
    })
], VegaText);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaText, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-text',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['overflow']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaTextarea = class VegaTextarea {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['vegaChange', 'vegaValidate', 'vegaBlur']);
    }
};
VegaTextarea.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaTextarea, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaTextarea.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaTextarea, selector: "vega-textarea", inputs: { autoValidation: "autoValidation", disabled: "disabled", hint: "hint", isValid: "isValid", label: "label", maxLength: "maxLength", minLength: "minLength", placeholder: "placeholder", required: "required", showCounter: "showCounter", validationRules: "validationRules", value: "value" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaTextarea = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['autoValidation', 'disabled', 'hint', 'isValid', 'label', 'maxLength', 'minLength', 'placeholder', 'required', 'showCounter', 'validationRules', 'value'],
        methods: ['valid']
    })
], VegaTextarea);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaTextarea, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-textarea',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['autoValidation', 'disabled', 'hint', 'isValid', 'label', 'maxLength', 'minLength', 'placeholder', 'required', 'showCounter', 'validationRules', 'value']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaTimePicker = class VegaTimePicker {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['vegaChange', 'vegaValidate']);
    }
};
VegaTimePicker.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaTimePicker, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaTimePicker.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaTimePicker, selector: "vega-time-picker", inputs: { autoValidation: "autoValidation", disabled: "disabled", increments: "increments", isValid: "isValid", label: "label", placeholder: "placeholder", positionRelativeTo: "positionRelativeTo", required: "required", size: "size", timeFormat: "timeFormat", validationRules: "validationRules", value: "value" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaTimePicker = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['autoValidation', 'disabled', 'increments', 'isValid', 'label', 'placeholder', 'positionRelativeTo', 'required', 'size', 'timeFormat', 'validationRules', 'value'],
        methods: ['valid']
    })
], VegaTimePicker);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaTimePicker, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-time-picker',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['autoValidation', 'disabled', 'increments', 'isValid', 'label', 'placeholder', 'positionRelativeTo', 'required', 'size', 'timeFormat', 'validationRules', 'value']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaToggleSwitch = class VegaToggleSwitch {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['vegaChange']);
    }
};
VegaToggleSwitch.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaToggleSwitch, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaToggleSwitch.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaToggleSwitch, selector: "vega-toggle-switch", inputs: { checked: "checked", disabled: "disabled", label: "label", size: "size" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaToggleSwitch = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['checked', 'disabled', 'label', 'size']
    })
], VegaToggleSwitch);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaToggleSwitch, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-toggle-switch',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['checked', 'disabled', 'label', 'size']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaTooltip = class VegaTooltip {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
VegaTooltip.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaTooltip, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaTooltip.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaTooltip, selector: "vega-tooltip", inputs: { content: "content", maxWidth: "maxWidth", minWidth: "minWidth", placement: "placement", positionRelativeTo: "positionRelativeTo", size: "size", trigger: "trigger" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaTooltip = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['content', 'maxWidth', 'minWidth', 'placement', 'positionRelativeTo', 'size', 'trigger']
    })
], VegaTooltip);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaTooltip, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-tooltip',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['content', 'maxWidth', 'minWidth', 'placement', 'positionRelativeTo', 'size', 'trigger']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let VegaVirtualScroll = class VegaVirtualScroll {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
VegaVirtualScroll.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaVirtualScroll, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
VegaVirtualScroll.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.2", type: VegaVirtualScroll, selector: "vega-virtual-scroll", inputs: { approxItemHeight: "approxItemHeight", renderItem: "renderItem", source: "source", sourceItemIdentifier: "sourceItemIdentifier", thresholdOfWork: "thresholdOfWork", viewportMaxHeight: "viewportMaxHeight" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
VegaVirtualScroll = __decorate([
    ProxyCmp({
        defineCustomElementFn: undefined,
        inputs: ['approxItemHeight', 'renderItem', 'source', 'sourceItemIdentifier', 'thresholdOfWork', 'viewportMaxHeight'],
        methods: ['scrollToIndex']
    })
], VegaVirtualScroll);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaVirtualScroll, decorators: [{
            type: Component,
            args: [{
                    selector: 'vega-virtual-scroll',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    inputs: ['approxItemHeight', 'renderItem', 'source', 'sourceItemIdentifier', 'thresholdOfWork', 'viewportMaxHeight']
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
var VegaComponents = [
    VegaAccordion,
    VegaAppFooter,
    VegaAppHeaderButton,
    VegaBadge,
    VegaBanner,
    VegaBarChart,
    VegaBox,
    VegaButton,
    VegaButtonCircle,
    VegaButtonLink,
    VegaCard,
    VegaCarousel,
    VegaCheckbox,
    VegaCheckboxGroup,
    VegaChip,
    VegaColorPicker,
    VegaComboBox,
    VegaCounterBadge,
    VegaDatePicker,
    VegaDialog,
    VegaDropdown,
    VegaFieldLabel,
    VegaFlex,
    VegaFooter,
    VegaForm,
    VegaGrid,
    VegaHeader,
    VegaIcon,
    VegaImageUploader,
    VegaInput,
    VegaInputCreditCard,
    VegaInputSelect,
    VegaItemToggle,
    VegaLeftNav,
    VegaLeftNavGroup,
    VegaLeftNavLink,
    VegaLeftNavSection,
    VegaLineChart,
    VegaLoadingIndicator,
    VegaModal,
    VegaOption,
    VegaPageNotification,
    VegaPagination,
    VegaPieChart,
    VegaPopover,
    VegaProgressTracker,
    VegaRadio,
    VegaRadioGroup,
    VegaSidenav,
    VegaSidenavGroup,
    VegaSidenavLink,
    VegaStepper,
    VegaTabGroup,
    VegaTabGroupPanel,
    VegaTable,
    VegaText,
    VegaTextarea,
    VegaTimePicker,
    VegaToggleSwitch,
    VegaTooltip,
    VegaVirtualScroll,
];

class VegaComponentModule {
    constructor() {
        this.useJuggleResizeObserver();
        defineCustomElements(window).then();
    }
    useJuggleResizeObserver() {
        if (FeatureFlag.isEnabled('VEGA_ANGULAR.USE_JUGGLE_RESIZE_OBSERVER')) {
            Object.assign(window, {
                'VegaResizeObserver': ResizeObserver
            });
        }
    }
}
VegaComponentModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaComponentModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
VegaComponentModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaComponentModule, declarations: [VegaAccordion, VegaAppFooter, VegaAppHeaderButton, VegaBadge, VegaBanner, VegaBarChart, VegaBox, VegaButton, VegaButtonCircle, VegaButtonLink, VegaCard, VegaCarousel, VegaCheckbox, VegaCheckboxGroup, VegaChip, VegaColorPicker, VegaComboBox, VegaCounterBadge, VegaDatePicker, VegaDialog, VegaDropdown, VegaFieldLabel, VegaFlex, VegaFooter, VegaForm, VegaGrid, VegaHeader, VegaIcon, VegaImageUploader, VegaInput, VegaInputCreditCard, VegaInputSelect, VegaItemToggle, VegaLeftNav, VegaLeftNavGroup, VegaLeftNavLink, VegaLeftNavSection, VegaLineChart, VegaLoadingIndicator, VegaModal, VegaOption, VegaPageNotification, VegaPagination, VegaPieChart, VegaPopover, VegaProgressTracker, VegaRadio, VegaRadioGroup, VegaSidenav, VegaSidenavGroup, VegaSidenavLink, VegaStepper, VegaTabGroup, VegaTabGroupPanel, VegaTable, VegaText, VegaTextarea, VegaTimePicker, VegaToggleSwitch, VegaTooltip, VegaVirtualScroll, TextValueAccessor, ValueAccessor], exports: [VegaAccordion, VegaAppFooter, VegaAppHeaderButton, VegaBadge, VegaBanner, VegaBarChart, VegaBox, VegaButton, VegaButtonCircle, VegaButtonLink, VegaCard, VegaCarousel, VegaCheckbox, VegaCheckboxGroup, VegaChip, VegaColorPicker, VegaComboBox, VegaCounterBadge, VegaDatePicker, VegaDialog, VegaDropdown, VegaFieldLabel, VegaFlex, VegaFooter, VegaForm, VegaGrid, VegaHeader, VegaIcon, VegaImageUploader, VegaInput, VegaInputCreditCard, VegaInputSelect, VegaItemToggle, VegaLeftNav, VegaLeftNavGroup, VegaLeftNavLink, VegaLeftNavSection, VegaLineChart, VegaLoadingIndicator, VegaModal, VegaOption, VegaPageNotification, VegaPagination, VegaPieChart, VegaPopover, VegaProgressTracker, VegaRadio, VegaRadioGroup, VegaSidenav, VegaSidenavGroup, VegaSidenavLink, VegaStepper, VegaTabGroup, VegaTabGroupPanel, VegaTable, VegaText, VegaTextarea, VegaTimePicker, VegaToggleSwitch, VegaTooltip, VegaVirtualScroll, TextValueAccessor, ValueAccessor] });
VegaComponentModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaComponentModule, imports: [[]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: VegaComponentModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...VegaComponents, TextValueAccessor, ValueAccessor],
                    imports: [],
                    exports: [...VegaComponents, TextValueAccessor, ValueAccessor],
                }]
        }], ctorParameters: function () { return []; } });

/*
 * Public API Surface of vega-angular
 */

/**
 * Generated bundle index. Do not edit.
 */

export { TextValueAccessor, ValueAccessor, VegaAccordion, VegaAppFooter, VegaAppHeaderButton, VegaBadge, VegaBanner, VegaBarChart, VegaBox, VegaButton, VegaButtonCircle, VegaButtonLink, VegaCard, VegaCarousel, VegaCheckbox, VegaCheckboxGroup, VegaChip, VegaColorPicker, VegaComboBox, VegaComponentModule, VegaCounterBadge, VegaDatePicker, VegaDialog, VegaDropdown, VegaFieldLabel, VegaFlex, VegaFooter, VegaForm, VegaGrid, VegaHeader, VegaIcon, VegaImageUploader, VegaInput, VegaInputCreditCard, VegaInputSelect, VegaItemToggle, VegaLeftNav, VegaLeftNavGroup, VegaLeftNavLink, VegaLeftNavSection, VegaLineChart, VegaLoadingIndicator, VegaModal, VegaOption, VegaPageNotification, VegaPagination, VegaPieChart, VegaPopover, VegaProgressTracker, VegaRadio, VegaRadioGroup, VegaSidenav, VegaSidenavGroup, VegaSidenavLink, VegaStepper, VegaTabGroup, VegaTabGroupPanel, VegaTable, VegaText, VegaTextarea, VegaTimePicker, VegaToggleSwitch, VegaTooltip, VegaVirtualScroll };
//# sourceMappingURL=heartlandone-vega-angular.mjs.map
